import { SelectOption } from '@ardoq/select';
import { isPerspectiveGroupingRuleWithWorkspace } from '@ardoq/perspectives-sidebar';
import {
  PerspectiveGroupingRule,
  PerspectivesGroupsOptions,
  GroupingRuleSelectOptions,
  ParentGroupingRule,
  ReferenceGroupingRule,
  FieldGroupingRule,
  ChildTypeGroupingRule,
  TagGroupingRule,
} from '@ardoq/perspectives';
import { ArdoqId, GroupType } from '@ardoq/api-types';
import { exhaustiveCheck } from '@ardoq/common-helpers';
import type { GroupingRuleLabelsByRuleId } from 'streams/grouping/getGroupingRuleLabelsByRuleId';

type ValidGroupOptionIds = {
  validWorkspaceIds: ArdoqId[];
  validComponentTypeIds: ArdoqId[];
  validReferenceTypeIds: number[];
  validFieldIds: ArdoqId[];
  validTagIds: ArdoqId[];
  validReferenceTypeNames: string[];
  validComponentTypeNames: string[];
};

const getValidGroupOptionIds = (groupsOptions: PerspectivesGroupsOptions) => {
  const validWorkspaceIds = groupsOptions.workspaces.map(
    workspace => workspace.id
  );
  const validComponentTypeIds = Object.values(
    groupsOptions.componentTypesPerWorkspace
  )
    .flat()
    .map(componentType => componentType.id);

  const validReferenceTypeIds = Object.values(
    groupsOptions.referenceTypesPerWorkspace
  )
    .flat()
    .map(referenceType => referenceType.id);

  const validFieldIds = Object.values(groupsOptions.fieldsPerWorkspace)
    .flat()
    .map(field => field.id);

  const validTagIds = Object.values(groupsOptions.tagsPerWorkspace)
    .flat()
    .map(tag => tag.id);

  const validReferenceTypeNames = groupsOptions.globalReferenceTypeNames;
  const validComponentTypeNames = groupsOptions.globalComponentTypeNames;

  return {
    validWorkspaceIds,
    validComponentTypeIds,
    validReferenceTypeIds,
    validFieldIds,
    validTagIds,
    validReferenceTypeNames,
    validComponentTypeNames,
  };
};

const isGroupingRuleBasedOnValidOptions = (
  groupingRule: PerspectiveGroupingRule,
  validGroupOptionIds: ValidGroupOptionIds
) => {
  const {
    validWorkspaceIds,
    validComponentTypeIds,
    validReferenceTypeIds,
    validFieldIds,
    validTagIds,
    validReferenceTypeNames,
  } = validGroupOptionIds;

  switch (groupingRule.type) {
    case GroupType.WORKSPACE:
    case GroupType.COMPONENT:
    case GroupType.PARENT_ALL:
      return true;

    case GroupType.REFERENCE_TYPE_NAME:
      return validReferenceTypeNames.includes(groupingRule.targetId);

    case GroupType.REFERENCE:
      return hasValidWorkspaceIdAndTargetId(
        groupingRule,
        validWorkspaceIds,
        validReferenceTypeIds.map(id => id?.toString())
      );
    case GroupType.PARENT:
      return hasValidWorkspaceIdAndTargetId(
        groupingRule,
        validWorkspaceIds,
        validComponentTypeIds
      );
    case GroupType.TAG:
      return hasValidWorkspaceIdAndTargetId(
        groupingRule,
        validWorkspaceIds,
        validTagIds
      );
    case GroupType.FIELD:
      return hasValidWorkspaceIdAndTargetId(
        groupingRule,
        validWorkspaceIds,
        validFieldIds
      );
    case GroupType.CHILD:
      return hasValidWorkspaceIdAndTargetId(
        groupingRule,
        validWorkspaceIds,
        validComponentTypeIds
      );
    default:
      return exhaustiveCheck(groupingRule);
  }
};

const hasValidWorkspaceIdAndTargetId = (
  groupingRule:
    | ParentGroupingRule
    | ReferenceGroupingRule
    | FieldGroupingRule
    | ChildTypeGroupingRule
    | TagGroupingRule,
  validWorkspaceIds: ArdoqId[],
  validTargetIds: (ArdoqId | number)[]
) =>
  validWorkspaceIds.includes(groupingRule.workspaceId) &&
  (!groupingRule.targetId || validTargetIds.includes(groupingRule.targetId));

const getGroupingRulesBasedOnInvalidOptions = (
  groupingRules: PerspectiveGroupingRule[],
  groupsOptions: PerspectivesGroupsOptions
): PerspectiveGroupingRule[] => {
  const validGroupOptionIds = getValidGroupOptionIds(groupsOptions);
  return groupingRules.filter(
    groupingRule =>
      !isGroupingRuleBasedOnValidOptions(groupingRule, validGroupOptionIds)
  );
};

const buildOption = (value: string, label: string): SelectOption<string> => ({
  value,
  label,
  isDisabled: true,
});

type AddRuleTargetSelectOptionParams = {
  options: Record<string, GroupingRuleSelectOptions>;
  rule: PerspectiveGroupingRule;
  targetId: string;
  label: string;
};

const addRuleTargetSelectOption = ({
  options,
  rule,
  targetId,
  label,
}: AddRuleTargetSelectOptionParams) => {
  options[rule.id] = {
    ...(options[rule.id] || {}),
    ruleTargetSelectOption: buildOption(targetId, label),
  };
  return options;
};

export const getInvalidGroupingRuleSelectOptionsByRuleId = (
  groupingRules: PerspectiveGroupingRule[],
  groupingOptions: PerspectivesGroupsOptions,
  labelsByRuleId: GroupingRuleLabelsByRuleId
) => {
  const invalidGroupingRules = getGroupingRulesBasedOnInvalidOptions(
    groupingRules,
    groupingOptions
  );

  return invalidGroupingRules.reduce<Record<string, GroupingRuleSelectOptions>>(
    (options, rule) => {
      if (rule.type === GroupType.REFERENCE_TYPE_NAME) {
        return addRuleTargetSelectOption({
          options,
          rule,
          targetId: rule.targetId,
          label: rule.targetId,
        });
      }

      if (!isPerspectiveGroupingRuleWithWorkspace(rule)) {
        return options;
      }

      const ruleLabels = labelsByRuleId[rule.id];
      const workspaceName = ruleLabels?.workspaceName;
      if (!workspaceName) {
        return options;
      }

      const hasValidWorkspace = groupingOptions.workspaces.some(
        workspace => workspace.id === rule.workspaceId
      );

      const workspaceSelectOption = !hasValidWorkspace
        ? buildOption(rule.workspaceId, workspaceName)
        : undefined;

      if (workspaceSelectOption) {
        options[rule.id] = {
          workspaceSelectOption,
        };
      }

      const targetId = rule.targetId;

      if (!targetId) {
        return {
          ...options,
          [rule.id]: {
            workspaceSelectOption,
          },
        };
      }

      const targetLabel = ruleLabels.targetLabel;

      switch (rule.type) {
        case GroupType.REFERENCE:
        case GroupType.PARENT:
        case GroupType.TAG:
        case GroupType.FIELD: {
          if (targetLabel) {
            return addRuleTargetSelectOption({
              options,
              rule,
              targetId,
              label: targetLabel,
            });
          }
          break;
        }
        default:
          exhaustiveCheck(rule);
      }

      return options;
    },
    {}
  );
};
